// The member frame in the JobGPT family look: a white sidebar with soft colour
// tiles and navy for the page you are on, a slim white header, grey canvas.
// Type follows tryjobgpt.com's signed-in pages: Outfit only, 14px/600 nav,
// 11px/800 labels, 800-weight headings, no serif and nothing heavier.
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  ClipboardList,
  Coins,
  FileText,
  Home,
  Linkedin,
  Lock,
  LogOut,
  Sparkles,
  ChevronDown,
  Mail,
  Target,
  User,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { useMembership } from "@/hooks/useMembership";
import { useToolAccess } from "@/hooks/useToolAccess";
import { useTailoringRoute } from "@/context/TailoringModeContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TailorWizLogo } from "@/components/brand/TailorWizLogo";
import { CrispAvatar } from "@/components/dashboard/CrispAvatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

type SidebarItem = { label: string; to: string; page: string[]; icon: React.ElementType; tile: string; toolId?: string };

export function StudioShell({
  children,
  currentPage,
  onSignOut,
}: {
  children: ReactNode;
  currentPage: string;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { balance, isCriticalBalance } = useCredits();
  const { planName } = useMembership();
  const { isToolLocked } = useToolAccess();
  const { route: tailoringRoute } = useTailoringRoute();

  const displayName =
    profile?.display_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    user?.email?.split("@")[0] ||
    "User";

  const groups: { label: string; items: SidebarItem[] }[] = [
    { label: "Dashboard", items: [
      { label: "Dashboard", to: "/", page: ["home"], icon: Home, tile: "text-[#000F36]" },
    ] },
    { label: "Resume Tools", items: [
      { label: "Resume Tailoring", to: tailoringRoute, page: ["resume-tailoring"], icon: Target, tile: "text-rose-500", toolId: "resume-tailoring" },
      { label: "Resume Analysis", to: "/?view=resume-analysis", page: ["resume-analysis"], icon: BarChart3, tile: "text-violet-500", toolId: "resume-analysis" },
      { label: "Metrics Wizard", to: "/metrics-wizard", page: ["metrics-wizard"], icon: BarChart3, tile: "text-orange-500", toolId: "metrics-wizard" },
      { label: "LinkedIn Wizard", to: "/linkedin-wizard-landing", page: ["linkedin-wizard"], icon: Linkedin, tile: "text-blue-500", toolId: "linkedin-wizard" },
      { label: "Job Wizard", to: "/qualification-wizard", page: ["job-wizard", "qualification-wizard"], icon: ClipboardList, tile: "text-emerald-500", toolId: "job-wizard" },
    ] },
    { label: "My Libraries", items: [
      { label: "Job Library", to: "/?view=job-library", page: ["job-library"], icon: Briefcase, tile: "text-amber-500" },
      { label: "Resume Library", to: "/?view=resume-library", page: ["resume-library"], icon: FileText, tile: "text-indigo-500" },
      { label: "LinkedIn Library", to: "/linkedin-profiles", page: ["linkedin-profiles"], icon: Linkedin, tile: "text-blue-500" },
    ] },
    { label: "Account", items: [
      { label: "Profile", to: "/profile", page: ["profile"], icon: User, tile: "text-slate-500" },
    ] },
  ];

  return (
    <TooltipProvider>
      <div className="min-h-screen flex w-full bg-[#F4F6F9] font-outfit">
        {/* Sidebar */}
        <aside className="w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col relative z-50">
          <button onClick={() => navigate("/")} className="flex items-center px-5 h-16 border-b border-slate-200" aria-label="TailorWiz home">
            <TailorWizLogo height={34} />
          </button>
          <nav className="px-3 py-5 space-y-5 overflow-y-auto">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="px-3 mb-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-slate-400">{g.label}</div>
                <div className="space-y-0.5">
                  {g.items.map((it) => {
                    const Icon = it.icon;
                    const active = it.page.includes(currentPage);
                    if (it.toolId && isToolLocked(it.toolId)) {
                      return (
                        <Tooltip key={it.label}>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-semibold text-slate-400 cursor-not-allowed">
                              <Icon className="w-4 h-4 shrink-0" strokeWidth={2.25} />
                              <span className="flex-1">{it.label}</span>
                              <Lock className="w-4 h-4 text-[#CD9700]" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="right">Upgrade to unlock this tool</TooltipContent>
                        </Tooltip>
                      );
                    }
                    return (
                      <button
                        key={it.label}
                        onClick={() => navigate(it.to)}
                        className={`group w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13.5px] font-semibold transition-all ${
                          active ? "bg-[#000F36] text-white shadow-[inset_3px_0_0_#CD9700]" : "text-slate-600 hover:bg-[#000F36] hover:text-white"
                        }`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 transition-colors ${active ? "text-[#CD9700]" : `${it.tile} group-hover:text-[#CD9700]`}`} strokeWidth={2.25} />
                        {it.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={onSignOut} className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13.5px] font-semibold text-slate-600 hover:bg-slate-100 transition-colors">
              <LogOut className="w-4 h-4 shrink-0 text-slate-500" strokeWidth={2.25} />
              Sign Out
            </button>
          </nav>
        </aside>

        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="bg-white border-b border-slate-200 px-8 h-16 flex items-center sticky top-0 z-40">
            <div className="flex items-center justify-between w-full gap-4">
              <h1 className="text-[18px] font-bold text-[#000F36]">
                {currentPage === "home" ? "Dashboard" : "TailorWiz"}
              </h1>
              <div className="flex items-center gap-2.5">
                <button
                  onClick={() => navigate("/pricing")}
                  title="Credits left. Click to add more."
                  className={`inline-flex items-center gap-2 h-9 rounded-lg border px-3 text-[13px] font-semibold whitespace-nowrap transition-colors ${
                    isCriticalBalance
                      ? "border-red-200 bg-red-50 text-red-700"
                      : "border-slate-200 bg-white text-[#000F36] hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  <Coins className="w-4 h-4 text-[#CD9700]" strokeWidth={2.25} />
                  <span className="tabular-nums">{balance}</span>
                  <span className="text-slate-500 font-medium">credits</span>
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  className="inline-flex items-center gap-1.5 h-9 rounded-lg bg-[#000F36] hover:bg-[#13245A] px-3.5 text-[13px] font-semibold text-white transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-[#CD9700]" strokeWidth={2.25} />
                  Upgrade
                </button>
                <div className="w-px h-6 bg-slate-200 mx-1.5" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-3 rounded-lg pl-1 pr-2 py-1 hover:bg-slate-50 transition-colors outline-none">
                      <span className="w-12 h-12 shrink-0 rounded-full p-[2px] bg-white ring-2 ring-[#CD9700] shadow-sm">
                        {profile?.avatar_url ? (
                          <CrispAvatar src={profile.avatar_url} alt={displayName} size={44} />
                        ) : (
                          <span className="w-full h-full rounded-full bg-[#000F36] text-white text-[16px] font-semibold flex items-center justify-center">
                            {displayName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>
                      <span className="hidden lg:flex flex-col items-start leading-tight">
                        <span className="text-[13px] font-semibold text-[#000F36]">{displayName}</span>
                        <span className="text-[12px] text-slate-500">{planName} plan</span>
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 font-outfit">
                    <DropdownMenuItem onClick={() => navigate("/profile")} className="text-[13px]">
                      <User className="w-4 h-4 mr-2 text-slate-500" /> My account
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/contact")} className="text-[13px]">
                      <Mail className="w-4 h-4 mr-2 text-slate-500" /> Contact us
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onSignOut} className="text-[13px]">
                      <LogOut className="w-4 h-4 mr-2 text-slate-500" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-hidden">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}
